/**
 * New Parcel Notes engine — May 17, 2026
 * =======================================
 *
 * Frontend mirror of `backend/services/new_parcel_notes.py`. Generates the
 * standardised "THIS IS A BRAND NEW PARCEL …" sentence that gets appended to
 * a parcel's notes block in the Tax Certificate preview and PDF when the
 * `New Parcel` toggle is enabled on the parcel details card.
 *
 * Output is BYTE-IDENTICAL to the Python implementation so the on-screen
 * preview and the generated PDF always render the same wording. Keep the
 * two files in lock-step when editing.
 *
 * The note lives ONLY in transient render output. It is never written back
 * into the editable notes field on the order document — see the user spec.
 * That keeps the standardised text safe from accidental edits.
 */

// ── Locked wording from the operations team ──────────────────────────
export const NEW_PARCEL_NOTE_PREFIX = 'THIS IS A BRAND NEW PARCEL';

// ── Date helpers ─────────────────────────────────────────────────────
const ISO_RE = /^\s*(\d{4})-(\d{1,2})-(\d{1,2})\s*$/;
const US_RE = /^\s*(\d{1,2})\/(\d{1,2})\/(\d{2,4})\s*$/;

const pad2 = (n) => String(n).padStart(2, '0');

/**
 * Normalise a date to `MM/DD/YYYY` for the PDF / preview.
 * Accepts `YYYY-MM-DD`, `MM/DD/YYYY`, `M/D/YY`. Returns the original
 * string unchanged if it does not match a known shape; empty string
 * for null / empty / non-string input.
 */
const formatDueDate = (raw) => {
  if (!raw || typeof raw !== 'string') return '';
  const s = raw.trim();
  if (!s) return '';
  let m = s.match(ISO_RE);
  if (m) {
    return `${pad2(parseInt(m[2], 10))}/${pad2(parseInt(m[3], 10))}/${m[1]}`;
  }
  m = s.match(US_RE);
  if (m) {
    let y = m[3];
    if (y.length === 2) y = `20${y}`;
    return `${pad2(parseInt(m[1], 10))}/${pad2(parseInt(m[2], 10))}/${y}`;
  }
  return s;
};

/** First installment dict across the parcel's jurisdictions, or null. */
const firstInstallment = (parcel) => {
  if (!parcel || typeof parcel !== 'object') return null;
  for (const jur of parcel.jurisdictions || []) {
    if (!jur || typeof jur !== 'object') continue;
    for (const inst of jur.installments || []) {
      if (inst && typeof inst === 'object') return inst;
    }
  }
  return null;
};

// ── Generator ────────────────────────────────────────────────────────
/**
 * Return the standardised New Parcel sentence for `parcel`, or '' when
 * `is_new_parcel` is not truthy. Safe to call on every parcel without
 * guarding at the call-site.
 */
export const generateNewParcelNote = (parcel) => {
  if (!parcel || typeof parcel !== 'object') return '';
  if (!parcel.is_new_parcel) return '';

  const first = firstInstallment(parcel) || {};

  const yearOverride = typeof parcel.new_parcel_first_year === 'string'
    ? parcel.new_parcel_first_year.trim()
    : (parcel.new_parcel_first_year != null ? String(parcel.new_parcel_first_year).trim() : '');
  const yearFromRow = first.tax_year != null ? String(first.tax_year).trim() : '';
  const year = yearOverride || yearFromRow || '____';

  const dateOverride = typeof parcel.new_parcel_first_due_date === 'string'
    ? parcel.new_parcel_first_due_date.trim()
    : (parcel.new_parcel_first_due_date != null ? String(parcel.new_parcel_first_due_date).trim() : '');
  const dateFromRow = first.due_date != null ? String(first.due_date).trim() : '';
  const dateRaw = dateOverride || dateFromRow;
  const date = formatDueDate(dateRaw) || '____';

  return (
    `${NEW_PARCEL_NOTE_PREFIX}, TAXES ARE BEING BILLED FROM ${year} ` +
    `TAX YEAR AND THERE IS NO PRIOR HISTORY, HENCE UPDATED AMOUNT ` +
    `AS $0.00. THE FIRST BILL WILL BE DUE ON ${date}.`
  );
};

// ── Idempotent appender ──────────────────────────────────────────────
const STALE_LINE_RE = new RegExp(
  `^\\s*${NEW_PARCEL_NOTE_PREFIX.replace(/[.*+?^${}()|[\\]\\\\]/g, '\\$&')}[^\\n]*\\.?\\s*$`,
  'gim'
);

const stripStaleNewParcel = (notes) => {
  if (!notes) return '';
  let cleaned = String(notes).replace(STALE_LINE_RE, '');
  cleaned = cleaned.replace(/\n{3,}/g, '\n\n');
  return cleaned.trim();
};

/**
 * Append the New Parcel sentence for `parcel` to existing `engineNotes`.
 * Idempotent — any stale sentence already in `engineNotes` is removed
 * before the fresh one is appended.
 */
export const appendNewParcelNote = (engineNotes, parcel) => {
  const sentence = generateNewParcelNote(parcel);
  const cleaned = stripStaleNewParcel(engineNotes);
  if (!sentence) return cleaned;
  if (!cleaned) return sentence;
  return `${cleaned}\n\n${sentence}`;
};

// Exported only for unit tests; not consumed elsewhere.
export const __testables__ = { formatDueDate, firstInstallment, stripStaleNewParcel };
